/** Formateo para la interfaz. La fuente de verdad de horarios es la API. */

export function formatPrice(priceCents: number | null, currency: string): string | null {
  if (priceCents === null) return null;
  try {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(priceCents / 100);
  } catch {
    return `${(priceCents / 100).toFixed(0)} ${currency}`;
  }
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

const WEEKDAYS = ["Domingo", "Lunes", "Martes", "Miercoles", "Jueves", "Viernes", "Sabado"];

export function weekdayName(weekday: number): string {
  return WEEKDAYS[weekday] ?? "";
}

export function formatMinuteOfDay(minuteOfDay: number): string {
  const hours = Math.floor(minuteOfDay / 60);
  const minutes = minuteOfDay % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/** Fecha YYYY-MM-DD a texto legible, sin depender de la zona del navegador. */
export function formatDateLong(dateISO: string): string {
  const [year, month, day] = dateISO.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  return new Intl.DateTimeFormat("es-AR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(date);
}

export function formatDateTimeInZone(isoInstant: string, timeZone: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    // Sin esto, `es-AR` escribe "09:00 a. m." — el resto de la app muestra
    // las horas en 24 h, y la pantalla de confirmacion terminaba usando otro
    // formato que los horarios que la persona acababa de elegir.
    hour12: false,
    timeZone,
  }).format(new Date(isoInstant));
}

/** Fecha de hoy (YYYY-MM-DD) en la zona horaria indicada. */
export function todayInZone(timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  return parts;
}

export function addDays(dateISO: string, days: number): string {
  const [year, month, day] = dateISO.split("-").map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}

/** Hora (HH:mm) de un instante, vista desde una zona horaria. */
export function formatTimeInZone(isoInstant: string, timeZone: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  }).format(new Date(isoInstant));
}

/** Fecha corta (dd/mm) de un instante en una zona horaria. */
export function formatShortDateInZone(isoInstant: string, timeZone: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    timeZone,
  }).format(new Date(isoInstant));
}

/** Fecha YYYY-MM-DD de un instante, en una zona horaria. */
export function dateOnlyInZone(isoInstant: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).format(new Date(isoInstant));
}

/**
 * Convierte fecha + hora local del negocio a un instante ISO.
 *
 * El calculo definitivo lo hace el servidor; esto es solo para enviar el
 * instante correcto desde el formulario del panel.
 */
export function localInputToInstant(dateISO: string, time: string, timeZone: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  const [year, month, day] = dateISO.split("-").map(Number) as [number, number, number];
  const naive = Date.UTC(year, month - 1, day, hours ?? 0, minutes ?? 0);

  const offsetAt = (instant: number): number => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(new Date(instant));
    const read = (type: string): number =>
      Number(parts.find((part) => part.type === type)?.value ?? "0");
    return (
      Date.UTC(read("year"), read("month") - 1, read("day"), read("hour"), read("minute"), read("second")) -
      Math.floor(instant / 1000) * 1000
    );
  };

  const firstGuess = naive - offsetAt(naive);
  return new Date(naive - offsetAt(firstGuess)).toISOString();
}

export const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

/**
 * Identificador publico de un negocio, tal como viaja en la URL.
 *
 * Lo usan el alta (para proponer la direccion) y la portada (para no mandar a
 * `/Mi Negocio` cuando alguien escribe el nombre con mayusculas y espacios).
 */
export function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);
}
